<template>
  <header class="app-header compact">
    <div class="header-backdrop"></div>

    <v-container class="header-container">
      <!-- Calculator Context -->
      <div class="calculator-context">
        <se-button
          v-if="context.showBackButton"
          variant="text"
          style-set="set8"
          class="back-btn"
          @click="handleBack"
          aria-label="Go back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </se-button>

        <div class="context-info">
          <div class="context-header">
            <h2 class="context-title">{{ context.title || 'Calculator' }}</h2>

            <!-- Tags -->
            <div v-if="context.tags && context.tags.length > 0" class="context-tags">
              <span class="tag tag-beta">Beta</span>
              <span
                v-for="tag in context.tags"
                :key="tag.label"
                :class="['tag', `tag-${tag.variant || 'info'}`]"
              >
                <v-icon v-if="tag.icon" size="10">{{ tag.icon }}</v-icon>
                {{ tag.label }}
              </span>
            </div>
            <div v-else class="context-tags">
              <span class="tag tag-beta">Beta</span>
            </div>
          </div>

          <p v-if="context.subtitle" class="context-subtitle">
            {{ context.subtitle }}
          </p>
        </div>
      </div>

      <!-- Auth/Actions Section -->
      <div class="auth-section">
        <!-- Theme Toggle (Small Size) -->
        <theme-toggle size="small" />

        <!-- Save Button / Unsaved Indicator -->
        <template v-if="context.saveState">
          <!-- Unsaved State: Show Save Button -->
          <se-button
            v-if="context.saveState.type === 'unsaved'"
            variant="secondary"
            class="save-btn"
            @click="handleSaveClick"
          >
            <v-icon size="16">mdi-content-save</v-icon>
            <span>Save</span>
          </se-button>

          <!-- Saving State -->
          <div v-else-if="context.saveState.type === 'saving'" class="save-status saving">
            <v-icon size="14" class="mdi-spin">mdi-loading</v-icon>
            <span>Saving...</span>
          </div>

          <!-- Saved State -->
          <div v-else-if="context.saveState.type === 'saved'" class="save-status saved">
            <v-icon size="14">mdi-check-circle</v-icon>
            <span>Saved</span>
          </div>
        </template>

        <!-- Status Badge -->
        <div
          v-if="context.status"
          :class="['status-chip', `status-${context.status.type}`]"
          :aria-label="context.status.message"
          role="status"
          :title="context.status.message"
        >
          <v-icon size="14">
            {{ statusIcons[context.status.type] }}
          </v-icon>
          <span>{{ context.status.message }}</span>
        </div>


        <!-- Primary Actions -->
        <div
          v-for="action in primaryActions"
          :key="action.id"
          class="action-wrapper"
          :title="action.label"
        >
          <se-button
            :variant="action.variant || 'primary'"
            :disabled="action.disabled || action.loading"
            @click="action.onClick"
            class="action-btn"
            :aria-label="action.label"
          >
            <v-icon v-if="action.icon" size="16">{{ action.icon }}</v-icon>
            <span>{{ action.label }}</span>
          </se-button>

          <!-- Progress ring overlay for export -->
          <div v-if="action.loading && action.progress !== undefined" class="progress-ring">
            <svg viewBox="0 0 36 36" class="progress-svg">
              <circle
                cx="18"
                cy="18"
                r="15.915"
                class="progress-circle"
                :style="`stroke-dasharray: ${action.progress / 100 * 100} 100`"
              />
            </svg>
            <span class="progress-text">{{ action.progress }}%</span>
          </div>
        </div>

        <!-- Secondary Actions (Hidden on Mobile) -->
        <se-button
          v-for="action in secondaryActions"
          :key="action.id"
          :variant="action.variant || 'secondary'"
          :disabled="action.disabled"
          @click="action.onClick"
          class="action-btn hidden md:inline-flex"
        >
          <v-icon v-if="action.icon" size="16">{{ action.icon }}</v-icon>
          <span>{{ action.label }}</span>
        </se-button>

        <!-- Overflow Menu (Kebab) -->
        <div v-if="overflowActions.length > 0" class="overflow-menu">
          <se-button
            variant="text"
            @click="showOverflowMenu = !showOverflowMenu"
            aria-label="More actions"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </se-button>

          <div v-show="showOverflowMenu" class="overflow-dropdown">
            <se-button
              v-for="action in overflowActions"
              :key="action.id"
              variant="text"
              style-set="set8"
              class="overflow-menu-item"
              :disabled="action.disabled"
              @click="action.onClick(); showOverflowMenu = false"
            >
              <v-icon v-if="action.icon" size="16">{{ action.icon }}</v-icon>
              <span>{{ action.label }}</span>
            </se-button>
          </div>
        </div>
      </div>
    </v-container>
  </header>

  <!-- Save to Project Dialog -->
  <save-to-project-dialog
    v-model="showSaveDialog"
    :calculation-id="context.saveState?.calculationId"
    @saved="handleSaved"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import SeButton from '@/components/SEButton.vue'
import ThemeToggle from '@/components/ui/ThemeToggle.vue'
import SaveToProjectDialog from '@/components/dialogs/SaveToProjectDialog.vue'
import { useNavbarContext } from '@/composables/useNavbarContext'

const { context, setSaved } = useNavbarContext()
const showOverflowMenu = ref(false)
const showSaveDialog = ref(false)

const primaryActions = computed(() =>
  context.value.actions.filter(a => a.priority === 'primary')
)

const secondaryActions = computed(() =>
  context.value.actions.filter(a => a.priority === 'secondary')
)

const overflowActions = computed(() =>
  context.value.actions.filter(a => a.priority === 'overflow')
)

const statusIcons: Record<string, string> = {
  saved: 'mdi-check-circle',
  saving: 'mdi-loading mdi-spin',
  error: 'mdi-alert-circle',
  info: 'mdi-information'
}

// Handle Save button click
const handleSaveClick = () => {
  showSaveDialog.value = true
}

// Handle save complete
const handleSaved = (data: { projectId: string; projectName: string; title: string }) => {
  setSaved()
  // TODO: Show toast notification
  if (import.meta.env.DEV) console.log(`Saved "${data.title}" to ${data.projectName}`)
}

// Keyboard shortcut: Ctrl+S / Cmd+S
const handleKeydown = (event: KeyboardEvent) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 's') {
    event.preventDefault()
    if (context.value.saveState?.type === 'unsaved') {
      handleSaveClick()
    }
  }
}

// Click outside handler for overflow menu
const handleOutsideClick = (event: Event) => {
  const target = event.target as Element
  if (target.closest('.overflow-menu')) return
  if (showOverflowMenu.value) showOverflowMenu.value = false
}

onMounted(() => {
  document.addEventListener('click', handleOutsideClick)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('keydown', handleKeydown)
})

// Safe back navigation: avoid OAuth/login pages
const router = useRouter()
const handleBack = () => {
  const state = window.history.state as { back?: string } | null
  const prev = state?.back || ''
  const referrer = document.referrer || ''
  const looksExternal = (url: string) => url.startsWith('http')
  const isAuthPath = (path: string) => /(^\/login|^\/auth|google|accounts\.google\.com)/i.test(path)

  // If previous path looks like auth/oauth or external, go home
  if (!prev || looksExternal(prev) || isAuthPath(prev) || isAuthPath(referrer)) {
    router.push('/')
  } else {
    router.back()
  }
}
</script>

<style scoped>
/* Header */
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px; /* Compact height */
  z-index: var(--z-sticky);
  border-bottom: 1px solid var(--border-subtle);
}

.header-backdrop {
  position: absolute;
  inset: 0;
  background: var(--bg-surface);
  opacity: 1;
}

.header-container {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Calculator Context */
.calculator-context {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.back-btn {
  padding: 0.5rem;
  border-radius: var(--radius-md);
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: rgba(var(--accent-rgb), 0.1);
  color: var(--accent);
}

.context-info {
  flex: 1;
  min-width: 0;
}

.context-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.context-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.context-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-micro);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tag-code {
  background: rgba(var(--accent-rgb), 0.15);
  color: var(--accent);
  border: 1px solid var(--accent);
}

.tag-version {
  background: color-mix(in srgb, var(--text-primary) 5%, transparent);
  color: var(--text-muted);
  border: 1px solid var(--border-subtle);
}

.tag-info {
  background: color-mix(in srgb, var(--text-primary) 5%, transparent);
  color: var(--text-muted);
  border: 1px solid var(--border-subtle);
}

.tag-status {
  background: rgba(var(--accent-rgb), 0.15);
  color: var(--accent);
  border: 1px solid var(--accent);
}

.tag-beta {
  background: transparent;
  color: var(--accent);
  border: 1px solid var(--accent);
  font-weight: 700;
}

.context-subtitle {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin: 0.25rem 0 0 0;
}

/* Status Chip */
.status-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  font-size: var(--font-size-caption);
  font-weight: 500;
  background: color-mix(in srgb, var(--text-primary) 5%, transparent);
  border: 1px solid var(--border-subtle);
  color: var(--text-muted);
}

.status-chip.status-saved {
  color: var(--accent);
  background: rgba(var(--accent-rgb), 0.1);
  border-color: var(--accent);
}

.status-chip.status-saving {
  color: var(--text-primary);
}

.status-chip.status-error {
  color: var(--state-error);
  background: rgba(var(--error-rgb), 0.1);
  border-color: var(--state-error);
}

.status-chip.status-info {
  color: var(--text-primary);
}

/* Save Button & Status */
.save-btn {
  gap: 0.5rem;
  font-size: var(--font-size-sm);
  padding: 8px 16px !important;
  min-height: 32px;
}

.save-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  font-size: var(--font-size-caption);
  font-weight: 500;
}

.save-status.saving {
  color: var(--text-muted);
}

.save-status.saved {
  color: var(--accent);
  animation: fade-out 2s ease-in-out forwards;
}

@keyframes fade-out {
  0%, 70% { opacity: 1; }
  100% { opacity: 0.5; }
}

/* Auth/Actions Section */
.auth-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* Action Buttons */
.action-wrapper {
  position: relative;
  display: inline-flex;
}

.action-btn {
  font-size: var(--font-size-sm);
  padding: 8px 16px !important;
  min-height: 32px;
}

/* Progress Ring */
.progress-ring {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  pointer-events: none;
}

.progress-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.progress-circle {
  fill: none;
  stroke: var(--accent);
  stroke-width: 2;
  stroke-dashoffset: 0;
  transition: stroke-dasharray 0.3s ease;
}

.progress-text {
  position: absolute;
  font-size: var(--font-size-2xs);
  font-weight: 600;
  color: var(--accent);
}

/* Overflow Menu */
.overflow-menu {
  position: relative;
}

.overflow-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 200px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  z-index: var(--z-banner);
  overflow: hidden;
  animation: dropdown-appear 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes dropdown-appear {
  from { opacity: 0; transform: translateY(-10px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.overflow-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: background 0.2s;
}

.overflow-menu-item:hover:not(:disabled) {
  background: rgba(var(--accent-rgb), 0.1);
}

.overflow-menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive Utilities */
@media (max-width: 767px) {
  .hidden {
    display: none !important;
  }

  .context-title {
    font-size: var(--font-size-sm);
  }

  .tag-version,
  .tag-beta {
    display: none !important;
  }

  /* Compact Status Chip */
  .status-chip span {
    display: none;
  }

  .status-chip {
    padding: 0.5rem;
  }

  /* Compact Action Buttons */
  .action-btn span {
    display: none;
  }

  .action-btn {
    padding: 8px 8px !important;
    min-width: 32px;
  }
}

@media (min-width: 768px) {
  .md\:inline-flex {
    display: inline-flex !important;
  }
}
</style>
